interface MultiDeviceSectionProps {
  title: string;
  description: string;
  note?: string;
}

export function MultiDeviceSection({
  title,
  description,
  note,
}: MultiDeviceSectionProps) {
  return (
    <section className="py-16 md:py-24 overflow-hidden relative">
      {/* Ambient glow behind devices */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[500px] h-[300px] bg-warm-700/[0.07] rounded-full blur-[100px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20">
          <h2 className="text-2xl md:text-3xl font-bold text-warm-50 mb-4">
            {title}
          </h2>
          <p className="text-warm-300 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            {description}
          </p>
          {note && (
            <p className="text-warm-500 text-sm italic mt-3">{note}</p>
          )}
        </div>

        {/* Device Mockups */}
        <div className="flex items-end justify-center gap-4 sm:gap-6 lg:gap-10">
          {/* ── Phone ── */}
          <div className="device-float-1 hidden md:block relative z-30">
            <div className="device-hover transition-transform duration-300 ease-out">
              <div className="relative w-[180px] lg:w-[210px]">
                {/* Body */}
                <div className="relative rounded-[2.2rem] bg-[#1a1816] p-[5px] shadow-2xl shadow-black/60 ring-1 ring-white/[0.06]">
                  {/* Screen */}
                  <div className="relative rounded-[1.8rem] overflow-hidden bg-dark-bg aspect-[9/19]">
                    {/* Dynamic Island */}
                    <div className="absolute top-[6px] left-1/2 -translate-x-1/2 w-[66px] h-[18px] bg-black rounded-full z-10" />
                    {/* Status bar hint */}
                    <div className="absolute top-[9px] left-[12px] w-3 h-1 rounded-full bg-white/10 z-10" />
                    <div className="absolute top-[9px] right-[12px] w-5 h-1 rounded-full bg-white/10 z-10" />
                    {/* Screenshot */}
                    <img
                      src="/images/ade/ade-overview-mobile.png"
                      alt="CloseCode ADE mobile interface showing project overview"
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Tablet ── */}
          <div className="device-float-2 hidden sm:block relative z-20">
            <div className="device-hover transition-transform duration-300 ease-out">
              <div className="relative w-[250px] lg:w-[310px]">
                {/* Body */}
                <div className="relative rounded-[1.2rem] bg-[#1a1816] p-[5px] shadow-2xl shadow-black/50 ring-1 ring-white/[0.06]">
                  {/* Screen */}
                  <div className="relative rounded-[0.9rem] overflow-hidden bg-dark-bg aspect-[3/4]">
                    {/* Camera dot */}
                    <div className="absolute top-[7px] left-1/2 -translate-x-1/2 w-[6px] h-[6px] bg-dark-border rounded-full z-10 ring-1 ring-black/40" />
                    {/* Screenshot */}
                    <img
                      src="/images/ade/ade-overview-tablet.png"
                      alt="CloseCode ADE tablet interface with task management"
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Laptop ── */}
          <div className="device-float-3 relative z-10">
            <div className="device-hover transition-transform duration-300 ease-out">
              <div className="relative w-[290px] sm:w-[360px] lg:w-[500px]">
                {/* Screen bezel */}
                <div className="relative rounded-t-xl bg-[#1a1816] p-[5px] pt-[10px] shadow-2xl shadow-black/50 ring-1 ring-white/[0.06]">
                  {/* Screen */}
                  <div className="relative rounded-t-lg overflow-hidden bg-dark-bg aspect-[16/10]">
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70px] lg:w-[80px] h-[13px] bg-[#1a1816] rounded-b-xl z-10" />
                    {/* Camera in notch */}
                    <div className="absolute top-[3px] left-1/2 -translate-x-1/2 w-[5px] h-[5px] bg-dark-border rounded-full z-20 ring-1 ring-black/40" />
                    {/* Screenshot */}
                    <img
                      src="/images/ade/ade-overview.png"
                      alt="CloseCode ADE desktop interface with full workspace"
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                </div>

                {/* Hinge */}
                <div className="relative h-[6px] bg-gradient-to-b from-[#252220] to-[#1a1816]">
                  <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-warm-600/15 to-transparent" />
                </div>

                {/* Keyboard base */}
                <div className="relative mx-[-14px]">
                  <div className="h-[26px] bg-gradient-to-b from-[#222019] to-[#1a1816] rounded-b-xl shadow-lg shadow-black/20">
                    {/* Keyboard rows hint */}
                    <div className="absolute top-[5px] inset-x-[14px] h-[3px] rounded-full bg-white/[0.03]" />
                    <div className="absolute top-[11px] inset-x-[14px] h-[3px] rounded-full bg-white/[0.03]" />
                    {/* Trackpad */}
                    <div className="absolute bottom-[4px] left-1/2 -translate-x-1/2 w-[32%] h-[12px] bg-white/[0.04] rounded-[3px]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float-1 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes float-2 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-7px); }
        }
        @keyframes float-3 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        .device-float-1 { animation: float-1 5s ease-in-out infinite; }
        .device-float-2 { animation: float-2 6s ease-in-out infinite 0.7s; }
        .device-float-3 { animation: float-3 7s ease-in-out infinite 1.4s; }

        .device-hover:hover {
          transform: scale(1.04);
        }

        @media (prefers-reduced-motion: reduce) {
          .device-float-1,
          .device-float-2,
          .device-float-3 {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}
